import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PopoverProps {
  /** Accessible name of both the trigger (when it has no text of its own) and the panel. */
  label: string;
  trigger: ReactNode;
  triggerClassName?: string;
  align?: 'start' | 'end';
  children: (close: () => void) => ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const EDGE = 8;
const GAP = 8;

/**
 * A panel anchored to its trigger and drawn on <body>: the bar can sit on the desk layer behind the
 * windows, where an in-place panel would be covered by them. Focus is kept inside while it is open and
 * returns to the trigger when it closes.
 */
export function Popover({ label, trigger, triggerClassName, align = 'end', children }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState<{ left: number; top: number; arrow: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  function close(restoreFocus = true) {
    setOpen(false);
    setLayout(null);
    if (restoreFocus) triggerRef.current?.focus();
  }

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;
    const anchor = triggerRef.current.getBoundingClientRect();
    const width = panelRef.current.offsetWidth;
    const ideal = align === 'end' ? anchor.right - width : anchor.left;
    const left = Math.min(Math.max(EDGE, ideal), window.innerWidth - width - EDGE);
    const center = anchor.left + anchor.width / 2;
    setLayout({ left, top: anchor.bottom + GAP, arrow: Math.min(Math.max(16, center - left), width - 16) });
  }, [open, align]);

  // Focus can only move in once the panel is visible, which is after it has been placed.
  useEffect(() => {
    if (layout) panelRef.current?.focus();
  }, [layout !== null]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!layerRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onResize = () => close(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  function trapTab(event: KeyboardEvent) {
    if (event.key !== 'Tab') return;
    const items = [...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    if (items.length === 0) return event.preventDefault();
    const first = items[0]!;
    const last = items.at(-1)!;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const style = layout
    ? ({ ['--pop-left' as string]: `${layout.left}px`, ['--pop-top' as string]: `${layout.top}px`, ['--pop-arrow' as string]: `${layout.arrow}px` } as CSSProperties)
    : ({ visibility: 'hidden', left: 0, top: 0 } as CSSProperties);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div ref={layerRef} className="popover-layer" style={style} onKeyDown={trapTab}>
            <span className="popover-arrow" aria-hidden />
            <div
              ref={panelRef}
              id={panelId}
              className="popover"
              role="dialog"
              aria-label={label}
              tabIndex={-1}
              onBlur={(event) => {
                const next = event.relatedTarget as Node | null;
                if (next && !layerRef.current?.contains(next) && !triggerRef.current?.contains(next)) close(false);
              }}
            >
              {children(() => close())}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
